// Stage markers for the hike overview map.
//
// A "stage" is a day's worth of routes. Every day except the last gets a
// numbered marker at the point where it hands off to the next day, carrying
// the number of the day that STARTS there ("2" marks where day 2 begins) —
// the same boundary, and the same numbering, the planner marks with its
// draggable stage-connector badges (see plan_trail/services/stage-connectors.ts).
// This module mirrors that detection logic, but reads it off the flattened
// per-route GeoJSON features
// HikeMap.vue already has (each carrying `hikeDayNumber`, `ptv2PlannedTrailId`
// and `markerType`) instead of the planner's live in-memory route state.

/**
 * Given a hike's GeoJSON features already sorted into day/position order
 * (see HikeMap.vue#sortFeaturesByTrailOrder), return one Point feature per
 * day boundary — at the END point of that day's last route — tagged with
 * the number of the day that starts there. Hikes with fewer than two days (or a
 * filtered view containing only one day/route) yield no markers.
 */
export function buildStageMarkerFeatures(sortedFeatures) {
  if (!Array.isArray(sortedFeatures) || sortedFeatures.length === 0) {
    return [];
  }

  // Only main-line routes carry a day's end — alts, spurs and transport legs
  // are offshoots and often sort after the day's main route, which would
  // otherwise drag the stage marker to the alt's end point. Mirrors the
  // planner's isConnectableRoute.
  const multiLineFeatures = sortedFeatures.filter(
    (f) => f.geometry?.type === 'MultiLineString' && (f.properties?.routeStyle ?? 'route') === 'route'
  );

  // Walk routes in their sorted order, remembering each day's LAST main-line
  // route feature and the order days were first encountered in.
  const dayOrder = [];
  const lastRouteFeatureByDay = new Map();
  multiLineFeatures.forEach((feature) => {
    const dayNumber = feature.properties?.hikeDayNumber;
    if (dayNumber == null) return;
    if (!lastRouteFeatureByDay.has(dayNumber)) dayOrder.push(dayNumber);
    lastRouteFeatureByDay.set(dayNumber, feature);
  });

  if (dayOrder.length < 2) {
    return [];
  }

  const endMarkerByTrailId = new Map();
  sortedFeatures.forEach((feature) => {
    if (feature.geometry?.type !== 'Point' || feature.properties?.markerType !== 'end') return;
    const trailId = feature.properties?.ptv2PlannedTrailId;
    if (trailId == null) return;
    endMarkerByTrailId.set(String(trailId), feature);
  });

  const markers = [];
  // Every day but the last closes out with a stage marker, numbered with the
  // day that begins there — the final day's end is the trail finish pin, not
  // a stage boundary.
  dayOrder.slice(0, -1).forEach((dayNumber, index) => {
    const lastRouteFeature = lastRouteFeatureByDay.get(dayNumber);
    const trailId = lastRouteFeature?.properties?.ptv2PlannedTrailId;
    const endMarker = trailId != null ? endMarkerByTrailId.get(String(trailId)) : null;
    if (!endMarker) return;

    markers.push({
      type: 'Feature',
      geometry: endMarker.geometry,
      properties: { stageNumber: dayOrder[index + 1] },
    });
  });

  return markers;
}
